<template>
  <div class="p-4">
    <!-- Header Stats Cards -->
    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
      <!-- Today's Couriers -->
      <div class="bg-white rounded-lg shadow p-4">
        <div class="flex items-center">
          <div class="p-3 rounded-full bg-blue-100 text-blue-600">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"></path>
            </svg>
          </div>
          <div class="ml-4">
            <p class="text-gray-500 text-sm">{{ $t('Couriers Today') }}</p>
            <p class="text-2xl font-semibold text-gray-800">{{ stats.todayCouriers || 0 }}</p>
          </div>
        </div>
      </div>

      <!-- Total Shippings to Follow -->
      <div class="bg-white rounded-lg shadow p-4">
        <div class="flex items-center">
          <div class="p-3 rounded-full bg-green-100 text-green-600">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"></path>
            </svg>
          </div>
          <div class="ml-4">
            <p class="text-gray-500 text-sm">{{ $t('Total Assigned') }}</p>
            <p class="text-2xl font-semibold text-gray-800">{{ stats.totalAssigned || 0 }}</p>
          </div>
        </div>
      </div>

      <!-- Today's Actions -->
      <div class="bg-white rounded-lg shadow p-4">
        <div class="flex items-center">
          <div class="p-3 rounded-full bg-yellow-100 text-yellow-600">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"></path>
            </svg>
          </div>
          <div class="ml-4">
            <p class="text-gray-500 text-sm">{{ $t('Actions Today') }}</p>
            <p class="text-2xl font-semibold text-gray-800">{{ stats.todayActions || 0 }}</p>
          </div>
        </div>
      </div>

      <!-- Urgent Items -->
      <div class="bg-white rounded-lg shadow p-4">
        <div class="flex items-center">
          <div class="p-3 rounded-full bg-red-100 text-red-600">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
            </svg>
          </div>
          <div class="ml-4">
            <p class="text-gray-500 text-sm">{{ $t('Urgent') }}</p>
            <p class="text-2xl font-semibold text-gray-800">{{ stats.urgentCount || 0 }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Status Breakdown -->
    <div class="bg-white rounded-lg shadow p-4 mb-6">
      <h3 class="text-lg font-semibold mb-4">{{ $t('Status Breakdown') }}</h3>
      <div class="flex flex-wrap gap-3">
        <button
          v-for="(count, status) in stats.statusCounts"
          :key="status"
          @click="filterByStatus(status)"
          :class="[
            'px-4 py-2 rounded-full text-sm font-medium transition-colors',
            activeStatus === status ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          ]"
        >
          {{ status }}: {{ count }}
        </button>
        <button
          @click="filterByStatus(null)"
          :class="[
            'px-4 py-2 rounded-full text-sm font-medium transition-colors',
            activeStatus === null ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          ]"
        >
          {{ $t('All') }}
        </button>
      </div>
    </div>

    <!-- Filters -->
    <div class="bg-white rounded-lg shadow p-4 mb-6">
      <div class="flex flex-wrap gap-4 items-center">
        <div class="flex-1 min-w-[200px]">
          <input
            type="text"
            v-model="searchQuery"
            @input="debounceSearch"
            :placeholder="$t('Search by tracking, phone, name...')"
            class="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        <select
          v-model="filters.courier"
          @change="onFiltersChange"
          class="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
        >
          <option value="">{{ $t('All Couriers') }}</option>
          <option v-for="c in couriers" :key="c.courier._id" :value="c.courier._id">
            {{ c.courierName || c.courier.fullName }}
          </option>
        </select>
        <!-- Date Period Filter -->
        <select
          v-model="filters.datePeriod"
          @change="onDatePeriodChange"
          class="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
        >
          <option value="">{{ $t('All Time') }}</option>
          <option value="today">{{ $t('Today') }}</option>
          <option value="yesterday">{{ $t('Yesterday') }}</option>
          <option value="thisWeek">{{ $t('This Week') }}</option>
          <option value="thisMonth">{{ $t('This Month') }}</option>
          <option value="custom">{{ $t('Custom Range') }}</option>
        </select>
        <!-- Custom Date Range (shown when "Custom Range" is selected) -->
        <template v-if="filters.datePeriod === 'custom'">
          <div class="flex items-center gap-2">
            <input
              type="date"
              v-model="filters.dateFrom"
              @change="onFiltersChange"
              class="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
            />
            <span class="text-gray-500">{{ $t('to') }}</span>
            <input
              type="date"
              v-model="filters.dateTo"
              @change="onFiltersChange"
              class="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </template>
        <!-- Reset Filters Button -->
        <button
          v-if="hasActiveFilters"
          @click="resetFilters"
          class="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
        >
          <svg class="w-5 h-5 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
          {{ $t('Reset') }}
        </button>
        <button
          @click="refreshAll"
          class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <svg class="w-5 h-5 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
          </svg>
          {{ $t('Refresh') }}
        </button>
      </div>
    </div>

    <!-- Shippings Table -->
    <div class="bg-white rounded-lg shadow overflow-hidden">
      <div class="overflow-x-auto">
        <table class="min-w-full divide-y divide-gray-200">
          <thead class="bg-gray-50">
            <tr>
              <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{{ $t('Tracking') }}</th>
              <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{{ $t('Customer') }}</th>
              <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{{ $t('Courier') }}</th>
              <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{{ $t('Status') }}</th>
              <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{{ $t('Follow-ups') }}</th>
              <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{{ $t('Last Update') }}</th>
              <th class="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">{{ $t('Actions') }}</th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <tr v-if="loading">
              <td colspan="7" class="px-4 py-8 text-center">
                <div class="flex justify-center items-center">
                  <svg class="animate-spin h-8 w-8 text-blue-600" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span class="ml-2">{{ $t('Loading...') }}</span>
                </div>
              </td>
            </tr>
            <tr v-else-if="shippings.length === 0">
              <td colspan="7" class="px-4 py-8 text-center text-gray-500">
                {{ $t('No shippings found') }}
              </td>
            </tr>
            <tr
              v-else
              v-for="shipping in shippings"
              :key="shipping._id"
              class="hover:bg-gray-50 cursor-pointer"
              @click="openShippingDetail(shipping)"
            >
              <td class="px-4 py-3">
                <div class="text-sm font-medium text-blue-600">{{ shipping.trackingNumber }}</div>
                <div class="text-xs text-gray-500">{{ getOrderId(shipping) }}</div>
              </td>
              <td class="px-4 py-3">
                <div class="text-sm font-medium text-gray-900">{{ getCustomerName(shipping) }}</div>
                <div class="text-xs text-gray-500">{{ getCustomerPhone(shipping) }}</div>
                <div class="text-xs text-gray-400">{{ getCustomerCity(shipping) }}</div>
              </td>
              <td class="px-4 py-3">
                <div class="text-sm text-gray-900">{{ getCourierName(shipping) }}</div>
              </td>
              <td class="px-4 py-3">
                <span :class="getStatusClass(shipping.status)" class="px-2 py-1 text-xs font-medium rounded-full">
                  {{ shipping.status }}
                </span>
              </td>
              <td class="px-4 py-3">
                <div class="text-sm text-gray-900">{{ shipping.followUpCount || 0 }} {{ $t('actions') }}</div>
                <div v-if="shipping.lastFollowUp" class="text-xs text-gray-500">
                  {{ $moment(shipping.lastFollowUp).fromNow() }}
                </div>
              </td>
              <td class="px-4 py-3">
                <div class="text-sm text-gray-900">{{ $moment(shipping.updatedAt).format('DD/MM/YYYY') }}</div>
                <div class="text-xs text-gray-500">{{ $moment(shipping.updatedAt).format('HH:mm') }}</div>
              </td>
              <td class="px-4 py-3 text-center">
                <div class="flex justify-center gap-2">
                  <button
                    @click.stop="quickAction(shipping, 'call')"
                    class="p-2 text-green-600 hover:bg-green-100 rounded-full"
                    :title="$t('Mark as Called')"
                  >
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"></path>
                    </svg>
                  </button>
                  <button
                    @click.stop="openNoteModal(shipping)"
                    class="p-2 text-blue-600 hover:bg-blue-100 rounded-full"
                    :title="$t('Add Note')"
                  >
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
                    </svg>
                  </button>
                  <button
                    @click.stop="openStatusModal(shipping)"
                    class="p-2 text-yellow-600 hover:bg-yellow-100 rounded-full"
                    :title="$t('Change Status')"
                  >
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
                    </svg>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div class="bg-gray-50 px-4 py-3 flex items-center justify-between border-t border-gray-200">
        <div class="text-sm text-gray-700">
          {{ $t('Showing') }} {{ shippings.length }} {{ $t('of') }} {{ paginate.total }} {{ $t('results') }}
        </div>
        <div class="flex gap-2">
          <button
            @click="changePage('prev')"
            :disabled="paginate.currentpage <= 1"
            class="px-3 py-1 border rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
          >
            {{ $t('Previous') }}
          </button>
          <span class="px-3 py-1 text-sm">
            {{ paginate.currentpage }} / {{ paginate.lastpage }}
          </span>
          <button
            @click="changePage('next')"
            :disabled="paginate.currentpage >= paginate.lastpage"
            class="px-3 py-1 border rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
          >
            {{ $t('Next') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Note Modal -->
    <div v-if="showNoteModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-white rounded-lg p-6 w-full max-w-md">
        <h3 class="text-lg font-semibold mb-4">{{ $t('Add Follow-up Note') }}</h3>
        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-1">{{ $t('Tracking') }}</label>
          <p class="text-blue-600 font-medium">{{ getSelectedTrackingNumber() }}</p>
        </div>
        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-1">{{ $t('Contacted') }}</label>
          <select v-model="noteForm.contactedPerson" class="w-full px-3 py-2 border rounded-lg">
            <option value="none">{{ $t('None') }}</option>
            <option value="courier">{{ $t('Courier') }}</option>
            <option value="customer">{{ $t('Customer') }}</option>
            <option value="both">{{ $t('Both') }}</option>
          </select>
        </div>
        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-1">{{ $t('Result') }}</label>
          <select v-model="noteForm.result" class="w-full px-3 py-2 border rounded-lg">
            <option value="pending">{{ $t('Pending') }}</option>
            <option value="reached">{{ $t('Reached') }}</option>
            <option value="unreached">{{ $t('Unreached') }}</option>
            <option value="resolved">{{ $t('Resolved') }}</option>
            <option value="escalated">{{ $t('Escalated') }}</option>
          </select>
        </div>
        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-1">{{ $t('Note') }}</label>
          <textarea
            v-model="noteForm.note"
            rows="3"
            class="w-full px-3 py-2 border rounded-lg"
            :placeholder="$t('Enter your follow-up note...')"
          ></textarea>
        </div>
        <div class="flex justify-end gap-3">
          <button
            @click="showNoteModal = false"
            class="px-4 py-2 border rounded-lg hover:bg-gray-100"
          >
            {{ $t('Cancel') }}
          </button>
          <button
            @click="submitNote"
            class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            {{ $t('Save Note') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Status Change Modal -->
    <div v-if="showStatusModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-white rounded-lg p-6 w-full max-w-md">
        <h3 class="text-lg font-semibold mb-4">{{ $t('Change Shipping Status') }}</h3>
        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-1">{{ $t('Tracking') }}</label>
          <p class="text-blue-600 font-medium">{{ getSelectedTrackingNumber() }}</p>
        </div>
        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-1">{{ $t('Current Status') }}</label>
          <span :class="getStatusClass(getSelectedStatus())" class="px-2 py-1 text-xs font-medium rounded-full">
            {{ getSelectedStatus() }}
          </span>
        </div>
        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-1">{{ $t('New Status') }}</label>
          <select v-model="statusForm.newStatus" class="w-full px-3 py-2 border rounded-lg">
            <option value="">{{ $t('Select status...') }}</option>
            <option v-for="status in availableStatuses" :key="status" :value="status">
              {{ status }}
            </option>
          </select>
        </div>
        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-1">{{ $t('Note') }}</label>
          <textarea
            v-model="statusForm.note"
            rows="2"
            class="w-full px-3 py-2 border rounded-lg"
            :placeholder="$t('Reason for status change...')"
          ></textarea>
        </div>
        <div class="flex justify-end gap-3">
          <button
            @click="showStatusModal = false"
            class="px-4 py-2 border rounded-lg hover:bg-gray-100"
          >
            {{ $t('Cancel') }}
          </button>
          <button
            @click="submitStatusChange"
            :disabled="!statusForm.newStatus"
            class="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 disabled:opacity-50"
          >
            {{ $t('Change Status') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Shipping Detail Modal -->
    <div v-if="showDetailModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-white rounded-lg w-full max-w-4xl overflow-hidden" style="max-height: 90vh;">
        <div class="p-4 border-b flex justify-between items-center">
          <h3 class="text-lg font-semibold">{{ $t('Shipping Details') }} - {{ getSelectedTrackingNumber() }}</h3>
          <button @click="showDetailModal = false" class="text-gray-500 hover:text-gray-700">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>
        <div class="p-4 overflow-y-auto" style="max-height: calc(90vh - 120px);">
          <!-- Shipping Info -->
          <div class="grid grid-cols-2 gap-4 mb-6">
            <div>
              <h4 class="font-medium text-gray-700 mb-2">{{ $t('Customer Info') }}</h4>
              <p><strong>{{ $t('Name') }}:</strong> {{ getDetailCustomerName() }}</p>
              <p><strong>{{ $t('Phone') }}:</strong> {{ getDetailCustomerPhone() }}</p>
              <p><strong>{{ $t('City') }}:</strong> {{ getDetailCustomerCity() }}</p>
              <p><strong>{{ $t('Address') }}:</strong> {{ getDetailCustomerAddress() }}</p>
            </div>
            <div>
              <h4 class="font-medium text-gray-700 mb-2">{{ $t('Shipping Info') }}</h4>
              <p><strong>{{ $t('Status') }}:</strong>
                <span :class="getStatusClass(getDetailShippingStatus())" class="px-2 py-1 text-xs font-medium rounded-full">
                  {{ getDetailShippingStatus() }}
                </span>
              </p>
              <p><strong>{{ $t('Courier') }}:</strong> {{ getDetailCourierName() }}</p>
              <p><strong>{{ $t('Zone') }}:</strong> {{ getDetailZoneName() }}</p>
              <p><strong>{{ $t('Price') }}:</strong> {{ getDetailTotalPrice() }} MAD</p>
            </div>
          </div>

          <!-- Follow-up History -->
          <div class="mb-6">
            <h4 class="font-medium text-gray-700 mb-2">{{ $t('Follow-up History') }}</h4>
            <div v-if="getFollowUpHistoryLength() === 0" class="text-gray-500 text-sm">
              {{ $t('No follow-up actions yet') }}
            </div>
            <div v-else class="space-y-3">
              <div
                v-for="action in getFollowUpHistory()"
                :key="action._id"
                class="border-l-4 border-blue-500 pl-4 py-2 bg-gray-50 rounded-r"
              >
                <div class="flex justify-between items-start">
                  <div>
                    <span class="font-medium">{{ action.actionType }}</span>
                    <span v-if="action.result" class="ml-2 text-sm text-gray-600">({{ action.result }})</span>
                  </div>
                  <span class="text-xs text-gray-500">{{ $moment(action.createdAt).format('DD/MM/YYYY HH:mm') }}</span>
                </div>
                <p v-if="action.note" class="text-sm text-gray-600 mt-1">{{ action.note }}</p>
                <p class="text-xs text-gray-400 mt-1">{{ $t('By') }}: {{ getActionAgentName(action) }}</p>
              </div>
            </div>
          </div>

          <!-- Status History -->
          <div>
            <h4 class="font-medium text-gray-700 mb-2">{{ $t('Status History') }}</h4>
            <div v-if="getStatusHistoryLength() === 0" class="text-gray-500 text-sm">
              {{ $t('No status changes') }}
            </div>
            <div v-else class="space-y-2">
              <div
                v-for="history in getStatusHistory()"
                :key="history._id"
                class="flex items-center justify-between py-2 border-b"
              >
                <div>
                  <span :class="getStatusClass(history.status)" class="px-2 py-1 text-xs font-medium rounded-full">
                    {{ history.status }}
                  </span>
                  <span v-if="history.comment" class="ml-2 text-sm text-gray-600">{{ history.comment }}</span>
                </div>
                <div class="text-right">
                  <p class="text-xs text-gray-500">{{ $moment(history.date).format('DD/MM/YYYY HH:mm') }}</p>
                  <p class="text-xs text-gray-400">{{ getHistoryUserName(history) }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AgentDashboard',
  props: {
    currentUser: { type: Object },
    idWharhouse: { type: Object },
  },
  data() {
    return {
      loading: false,
      shippings: [],
      couriers: [],
      stats: {},
      searchQuery: '',
      activeStatus: null,
      filters: {
        courier: '',
        status: '',
        datePeriod: '',
        dateFrom: '',
        dateTo: ''
      },
      paginate: {
        total: 0,
        currentpage: 1,
        lastpage: 1,
        per_page: 50
      },
      showNoteModal: false,
      showStatusModal: false,
      showDetailModal: false,
      selectedShipping: null,
      shippingDetail: null,
      noteForm: {
        contactedPerson: 'none',
        result: 'pending',
        note: ''
      },
      statusForm: {
        newStatus: '',
        note: ''
      },
      availableStatuses: ['delivered', 'shipped', 'cancelled', 'refused', 'unreachable', 'remind', 'pending return', 'return', 'reprogrammer'],
      searchTimeout: null
    };
  },
  computed: {
    // Check if any filters are active
    hasActiveFilters() {
      return this.filters.courier !== '' ||
             this.filters.datePeriod !== '' ||
             this.searchQuery !== '' ||
             this.activeStatus !== null;
    }
  },
  async mounted() {
    if (this.currentUser && this.currentUser._id) {
      await this.loadStats();
      await this.loadCouriers();
      await this.loadShippings();
    }
  },
  methods: {
    // Safe accessor methods to replace optional chaining in templates
    getOrderId(shipping) {
      return shipping && shipping.orderObjet ? shipping.orderObjet.id : '';
    },
    getCustomerName(shipping) {
      if (shipping && shipping.orderObjet && shipping.orderObjet.customer) {
        return shipping.orderObjet.customer.fullName || '';
      }
      return '';
    },
    getCustomerPhone(shipping) {
      if (shipping && shipping.orderObjet && shipping.orderObjet.customer) {
        return shipping.orderObjet.customer.phone || '';
      }
      return '';
    },
    getCustomerCity(shipping) {
      if (shipping && shipping.orderObjet && shipping.orderObjet.customer) {
        return shipping.orderObjet.customer.city || '';
      }
      return '';
    },
    getCourierName(shipping) {
      return shipping && shipping.courier ? (shipping.courier.fullName || '-') : '-';
    },
    getSelectedTrackingNumber() {
      return this.selectedShipping ? this.selectedShipping.trackingNumber : '';
    },
    getSelectedStatus() {
      return this.selectedShipping ? this.selectedShipping.status : '';
    },
    // Detail modal accessors
    getDetailCustomerName() {
      if (this.shippingDetail && this.shippingDetail.shipping && this.shippingDetail.shipping.orderObjet && this.shippingDetail.shipping.orderObjet.customer) {
        return this.shippingDetail.shipping.orderObjet.customer.fullName || '';
      }
      return '';
    },
    getDetailCustomerPhone() {
      if (this.shippingDetail && this.shippingDetail.shipping && this.shippingDetail.shipping.orderObjet && this.shippingDetail.shipping.orderObjet.customer) {
        return this.shippingDetail.shipping.orderObjet.customer.phone || '';
      }
      return '';
    },
    getDetailCustomerCity() {
      if (this.shippingDetail && this.shippingDetail.shipping && this.shippingDetail.shipping.orderObjet && this.shippingDetail.shipping.orderObjet.customer) {
        return this.shippingDetail.shipping.orderObjet.customer.city || '';
      }
      return '';
    },
    getDetailCustomerAddress() {
      if (this.shippingDetail && this.shippingDetail.shipping && this.shippingDetail.shipping.orderObjet && this.shippingDetail.shipping.orderObjet.customer) {
        return this.shippingDetail.shipping.orderObjet.customer.address || '';
      }
      return '';
    },
    getDetailShippingStatus() {
      if (this.shippingDetail && this.shippingDetail.shipping) {
        return this.shippingDetail.shipping.status || '';
      }
      return '';
    },
    getDetailCourierName() {
      if (this.shippingDetail && this.shippingDetail.shipping && this.shippingDetail.shipping.courier) {
        return this.shippingDetail.shipping.courier.fullName || '-';
      }
      return '-';
    },
    getDetailZoneName() {
      if (this.shippingDetail && this.shippingDetail.shipping && this.shippingDetail.shipping.zone) {
        return this.shippingDetail.shipping.zone.name || '-';
      }
      return '-';
    },
    getDetailTotalPrice() {
      if (this.shippingDetail && this.shippingDetail.shipping && this.shippingDetail.shipping.orderObjet) {
        return this.shippingDetail.shipping.orderObjet.totalPrice || 0;
      }
      return 0;
    },
    getFollowUpHistory() {
      if (this.shippingDetail && this.shippingDetail.followUpHistory) {
        return this.shippingDetail.followUpHistory;
      }
      return [];
    },
    getFollowUpHistoryLength() {
      return this.getFollowUpHistory().length;
    },
    getActionAgentName(action) {
      if (action.agentName) {
        return action.agentName;
      }
      if (action.agent && action.agent.fullName) {
        return action.agent.fullName;
      }
      return '';
    },
    getStatusHistory() {
      if (this.shippingDetail && this.shippingDetail.statusHistory) {
        return this.shippingDetail.statusHistory;
      }
      return [];
    },
    getStatusHistoryLength() {
      return this.getStatusHistory().length;
    },
    getHistoryUserName(history) {
      return history && history.user ? history.user.fullName : '';
    },

    async loadStats() {
      try {
        const params = {};

        // Apply courier filter
        if (this.filters.courier) {
          params.courier = this.filters.courier;
        }

        // Apply date filters
        const dateRange = this.getDateRange();
        if (dateRange.dateFrom) {
          params.dateFrom = dateRange.dateFrom;
        }
        if (dateRange.dateTo) {
          params.dateTo = dateRange.dateTo;
        }

        const res = await this.$server.getDashboardStats('agentDashboard', params);
        if (res && res.content) {
          this.stats = res.content;
        } else if (res && !res.content) {
          this.stats = res;
        }
      } catch (error) {
        console.error('Error loading stats:', error);
      }
    },

    async loadCouriers() {
      try {
        const res = await this.$server.getMyCouriers('agentDashboard', {});
        if (res && res.content && res.content.couriers) {
          this.couriers = res.content.couriers;
        } else if (res && res.couriers) {
          this.couriers = res.couriers;
        }
      } catch (error) {
        console.error('Error loading couriers:', error);
      }
    },

    async loadShippings() {
      this.loading = true;
      try {
        const params = {
          page: this.paginate.currentpage,
          limit: this.paginate.per_page
        };

        if (this.filters.courier) {
          params.courier = this.filters.courier;
        }
        if (this.activeStatus) {
          params.status = this.activeStatus;
        }
        if (this.searchQuery) {
          params.search = this.searchQuery;
        }

        // Apply date filters
        const dateRange = this.getDateRange();
        if (dateRange.dateFrom) {
          params.dateFrom = dateRange.dateFrom;
        }
        if (dateRange.dateTo) {
          params.dateTo = dateRange.dateTo;
        }

        const res = await this.$server.getMyShippings('agentDashboard', params);
        if (res && res.content) {
          this.shippings = res.content.results || [];
          this.paginate.total = res.content.total || 0;
          this.paginate.lastpage = res.content.last_page || 1;
        } else if (res && res.results) {
          this.shippings = res.results || [];
          this.paginate.total = res.total || 0;
          this.paginate.lastpage = res.last_page || 1;
        }
      } catch (error) {
        console.error('Error loading shippings:', error);
        this.shippings = [];
      }
      this.loading = false;
    },

    debounceSearch() {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }
      this.searchTimeout = setTimeout(function() {
        this.paginate.currentpage = 1;
        this.loadShippings();
      }.bind(this), 500);
    },

    // Get date range based on selected period
    getDateRange() {
      const now = new Date();
      let dateFrom = '';
      let dateTo = '';

      switch (this.filters.datePeriod) {
        case 'today': {
          const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
          dateFrom = today.toISOString();
          dateTo = new Date(today.getTime() + 24 * 60 * 60 * 1000 - 1).toISOString();
          break;
        }
        case 'yesterday': {
          const yesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
          dateFrom = yesterday.toISOString();
          dateTo = new Date(yesterday.getTime() + 24 * 60 * 60 * 1000 - 1).toISOString();
          break;
        }
        case 'thisWeek': {
          const dayOfWeek = now.getDay();
          const diffToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
          const monday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - diffToMonday);
          dateFrom = monday.toISOString();
          dateTo = now.toISOString();
          break;
        }
        case 'thisMonth': {
          const firstOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
          dateFrom = firstOfMonth.toISOString();
          dateTo = now.toISOString();
          break;
        }
        case 'custom': {
          if (this.filters.dateFrom) {
            dateFrom = new Date(this.filters.dateFrom).toISOString();
          }
          if (this.filters.dateTo) {
            const endDate = new Date(this.filters.dateTo);
            endDate.setHours(23, 59, 59, 999);
            dateTo = endDate.toISOString();
          }
          break;
        }
        default:
          // No date filter
          break;
      }

      return { dateFrom, dateTo };
    },

    // Handle date period selection change
    onDatePeriodChange() {
      // Clear custom dates when switching to a preset
      if (this.filters.datePeriod !== 'custom') {
        this.filters.dateFrom = '';
        this.filters.dateTo = '';
      }
      this.onFiltersChange();
    },

    // Handle any filter change - update both shippings and stats
    onFiltersChange() {
      this.paginate.currentpage = 1;
      this.loadShippings();
      this.loadStats();
    },

    // Refresh all data
    refreshAll() {
      this.loadShippings();
      this.loadStats();
    },

    // Reset all filters to default
    resetFilters() {
      this.filters.courier = '';
      this.filters.datePeriod = '';
      this.filters.dateFrom = '';
      this.filters.dateTo = '';
      this.searchQuery = '';
      this.activeStatus = null;
      this.paginate.currentpage = 1;
      this.loadShippings();
      this.loadStats();
    },

    filterByStatus(status) {
      this.activeStatus = status;
      this.paginate.currentpage = 1;
      this.loadShippings();
      // Note: Stats are already filtered by courier/date, status just filters the list
    },

    changePage(direction) {
      if (direction === 'next' && this.paginate.currentpage < this.paginate.lastpage) {
        this.paginate.currentpage++;
      } else if (direction === 'prev' && this.paginate.currentpage > 1) {
        this.paginate.currentpage--;
      }
      this.loadShippings();
    },

    getStatusClass(status) {
      const classes = {
        'delivered': 'bg-green-100 text-green-800',
        'shipped': 'bg-blue-100 text-blue-800',
        'prepared': 'bg-indigo-100 text-indigo-800',
        'cancelled': 'bg-red-100 text-red-800',
        'refused': 'bg-red-100 text-red-800',
        'unreachable': 'bg-yellow-100 text-yellow-800',
        'remind': 'bg-purple-100 text-purple-800',
        'return': 'bg-orange-100 text-orange-800',
        'pending return': 'bg-orange-100 text-orange-800',
        'paid': 'bg-green-100 text-green-800',
        'processed': 'bg-green-100 text-green-800'
      };
      return classes[status] || 'bg-gray-100 text-gray-800';
    },

    async quickAction(shipping, type) {
      try {
        const actionData = {
          shipping: shipping._id,
          actionType: type === 'call' ? 'called_customer' : 'marked_followed_up',
          contactedPerson: type === 'call' ? 'customer' : 'none',
          result: 'pending'
        };

        const res = await this.$server.createAction('followUpActions', actionData);
        if (res && (res.content || res._id)) {
          alert(this.$t('Action recorded successfully'), 'success');
          this.loadShippings();
          this.loadStats();
        }
      } catch (error) {
        console.error('Error recording action:', error);
        alert(this.$t('Error recording action'));
      }
    },

    openNoteModal(shipping) {
      this.selectedShipping = shipping;
      this.noteForm = {
        contactedPerson: 'none',
        result: 'pending',
        note: ''
      };
      this.showNoteModal = true;
    },

    async submitNote() {
      try {
        const actionData = {
          shipping: this.selectedShipping._id,
          actionType: 'note_added',
          contactedPerson: this.noteForm.contactedPerson,
          result: this.noteForm.result,
          note: this.noteForm.note
        };

        const res = await this.$server.addNote('followUpActions', actionData);
        if (res && (res.content || res._id)) {
          alert(this.$t('Note added successfully'), 'success');
          this.showNoteModal = false;
          this.loadShippings();
          this.loadStats();
        }
      } catch (error) {
        console.error('Error adding note:', error);
        alert(this.$t('Error adding note'));
      }
    },

    openStatusModal(shipping) {
      this.selectedShipping = shipping;
      this.statusForm = {
        newStatus: '',
        note: ''
      };
      this.showStatusModal = true;
    },

    async submitStatusChange() {
      if (!this.statusForm.newStatus) return;

      try {
        const actionData = {
          shippingId: this.selectedShipping._id,
          newStatus: this.statusForm.newStatus,
          note: this.statusForm.note
        };

        const res = await this.$server.changeStatus('agentDashboard', actionData);
        if (res && (res.content || res._id)) {
          alert(this.$t('Status changed successfully'), 'success');
          this.showStatusModal = false;
          this.loadShippings();
          this.loadStats();
        }
      } catch (error) {
        console.error('Error changing status:', error);
        alert(this.$t('Error changing status'));
      }
    },

    async openShippingDetail(shipping) {
      this.selectedShipping = shipping;
      try {
        const res = await this.$server.getShippingDetails('agentDashboard', {
          shippingId: shipping._id
        });
        if (res && res.content) {
          this.shippingDetail = res.content;
          this.showDetailModal = true;
        } else if (res && res.shipping) {
          this.shippingDetail = res;
          this.showDetailModal = true;
        }
      } catch (error) {
        console.error('Error loading shipping details:', error);
      }
    }
  }
};
</script>

<style scoped>
</style>
